import { useCallback, useState } from "react";
import type { CrudRow } from "../types";

// ---------------------------------------------------------------------------
// Interactive, in-session CRUD state. Seeded with mock rows; add/update/remove
// mutate local React state only (resets on reload). This is the single seam a
// backend would replace later (swap the setters for API calls).
// ---------------------------------------------------------------------------

let counter = 0;
const nextId = () => `tmp-${Date.now().toString(36)}-${(counter++).toString(36)}`;

export interface CrudState<T extends CrudRow> {
  rows: T[];
  add: (row: Omit<T, "id">) => void;
  update: (id: string, patch: Partial<T>) => void;
  remove: (id: string) => void;
}

export function useCrudState<T extends CrudRow>(seed: T[]): CrudState<T> {
  const [rows, setRows] = useState<T[]>(seed);

  const add = useCallback((row: Omit<T, "id">) => {
    setRows((prev) => [{ ...(row as T), id: nextId() }, ...prev]);
  }, []);

  const update = useCallback((id: string, patch: Partial<T>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const remove = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  return { rows, add, update, remove };
}
